import { DataCluster } from "../DataCluster";
import { MarkerUtils } from "../MarkerUtils";
import { filterMarkersToPaddedViewport } from "./utils";

export interface AlgorithmInput {
    map: google.maps.Map;
    lstPoints: google.maps.Data.Feature[];
    mapCanvasProjection: google.maps.MapCanvasProjection;
    ForceRedraw: boolean;
}

export interface AlgorithmOutput {
    clusters: DataCluster[];
    changed?: boolean;
}

export interface Algorithm {
    calculate: ({ lstPoints, map }: AlgorithmInput) => AlgorithmOutput;
}

export interface AlgorithmOptions {
    maxZoom?: number;
}

export abstract class AbstractAlgorithm implements Algorithm{
    protected maxZoom: number;

    constructor({ maxZoom = 16}: AlgorithmOptions) {
        this.maxZoom = maxZoom;
    }

    protected noop<T extends Pick<AlgorithmInput, "lstPoints">>({
        lstPoints
    }: T): DataCluster[]{
        return noop(lstPoints);
    }

    public abstract calculate({ lstPoints, map}: AlgorithmInput): AlgorithmOutput;

    protected abstract cluster({ lstPoints, map }: AlgorithmInput): DataCluster[];
}

export interface ViewportAlgorithmOptions extends AlgorithmOptions {
    viewportPadding?: number;
}

export abstract class AbstractViewportAlgorithm extends AbstractAlgorithm{
    protected viewportPadding = 60;

    constructor({ viewportPadding = 60, ...options }: ViewportAlgorithmOptions){
        super(options);
        this.viewportPadding = viewportPadding;
    }

    public calculate({
        lstPoints,
        map,
        mapCanvasProjection,
        ForceRedraw
    }: AlgorithmInput): AlgorithmOutput {
        if(map.getZoom() >= this.maxZoom){
            return {
                clusters: this.noop({
                    lstPoints
                }),
                changed: false
            };
        }

        return {
            clusters: this.cluster({
                lstPoints: filterMarkersToPaddedViewport(
                    map,
                    mapCanvasProjection,
                    lstPoints,
                    this.viewportPadding
                ),
                map,
                mapCanvasProjection,
                ForceRedraw
            })
        };
    }

    protected abstract cluster({ lstPoints, map}: AlgorithmInput): DataCluster[];
}

export const noop = (lstPoints: google.maps.Data.Feature[]): DataCluster[] => {
    const clusters = lstPoints.map(
      (point) =>
        new DataCluster({
          position: MarkerUtils.getPosition(point),
          lstPoints: [point],
        })
    );
    return clusters;
  };
  